/**
 * useSeedDrawerTab Hook
 *
 * Local state management for seed drawer tabs with debounced URL sync.
 *
 * Problem: Tab alternation bug where React Router's setSearchParams causes race
 * conditions when mixed with arrow key navigation that rapidly updates URL params.
 *
 * Solution: Use local React state as the single source of truth for tab selection,
 * with debounced synchronization to URL for deep linking support.
 *
 * Pattern:
 * - Tab state lives in useState (immediate, no race conditions)
 * - Syncs to URL after 200ms debounce (prevents URL thrashing)
 * - Initializes from URL on mount (supports deep linking)
 * - Flushes to URL on unmount (ensures share URLs have correct tab)
 * - Listens to popstate for browser back/forward navigation
 *
 * @see useTechnologyDrawerTab for similar pattern
 * @see useAssetDrawerTab for reference drawer implementation
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

const URL_PARAM_NAME = 'seedDrawerTab';
const DEBOUNCE_DELAY_MS = 200;

/**
 * Hook for managing seed drawer tab state with local state as source of truth.
 *
 * @returns Tuple of [currentTab, setTab] similar to useState
 *
 * @example
 * ```tsx
 * function SeedDrawer() {
 *   const [currentTab, setCurrentTab] = useSeedDrawerTab();
 *
 *   return (
 *     <Tabs
 *       selected={currentTab}
 *       onSelect={tab => setCurrentTab(tab.value)}
 *     />
 *   );
 * }
 * ```
 */
export function useSeedDrawerTab(): [string, (tab: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state initialized from URL (preserves deep linking)
  const [localTab, setLocalTab] = useState<string>(() => {
    const urlTab = searchParams.get(URL_PARAM_NAME);
    return urlTab || '';
  });

  // Ref to track timeout for cleanup
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref to expose flush function for immediate sync
  const flushToURLRef = useRef<(() => void) | null>(null);

  // Debounced URL sync
  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Schedule debounced update
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchParams(
        params => {
          if (localTab) {
            params.set(URL_PARAM_NAME, localTab);
          } else {
            params.delete(URL_PARAM_NAME);
          }
          return params;
        },
        { replace: true }
      );
    }, DEBOUNCE_DELAY_MS);

    // Expose flush function for immediate sync
    flushToURLRef.current = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setSearchParams(
        params => {
          if (localTab) {
            params.set(URL_PARAM_NAME, localTab);
          } else {
            params.delete(URL_PARAM_NAME);
          }
          return params;
        },
        { replace: true }
      );
    };

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localTab, setSearchParams]);

  // Flush on unmount (ensures URL is correct when drawer closes)
  useEffect(() => {
    return () => {
      flushToURLRef.current?.();
    };
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const tabFromURL =
        new URLSearchParams(window.location.search).get(URL_PARAM_NAME) || '';
      setLocalTab(tabFromURL);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return [localTab, setLocalTab];
}
